import { useState, useRef, RefObject, ReactNode, isValidElement, cloneElement, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useOnClickOutside } from 'usehooks-ts';

/*
 * ⚠️ התוכן מרונדר ב-portal על ה-body וממוקם ב-fixed, ולא כ-absolute
 * בתוך עוטף relative (HAT-1170).
 *
 * מה זה פותר: שורת קפסולות הסינון במסך צר אמורה להיות שורה אחת
 * שנגללת אופקית — כך זה בעיצוב שממנו מעבירים (hatzdaka-nedarim,
 * `.filter-row`). אבל `overflow-x: auto` מכריח את הדפדפן לחשב גם
 * `overflow-y` כ-auto, וכל עוד התפריט הוא ילד של השורה הוא נחתך
 * בשורה נמוכה ונעלם. בלי שגיאה, בלי סימן — פשוט "הסינון לא עובד".
 * המקור נתקל בדיוק בזה ופתר אותו באותה דרך.
 *
 * מה **לא** משתנה: ה-API, סדר ה-DOM מבחינת המשתמש, ההתנהגות המבוקרת
 * (isOpen/setIsOpen), סגירה בלחיצה בחוץ, ו-closeOnContentClick.
 * הרכיב הזה משרת את כל הפילטרים בכל הלוחות, ולכן שינוי התנהגות בו
 * אינו חלק מהמשימה.
 */

interface DropdownProps {
  trigger: ReactNode;
  children: ReactNode;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  contentAlign?: 'start' | 'end';
  closeOnContentClick?: boolean;
  activeTriggerClassName?: string;
}

/** מרווח מהקצה, כדי שתפריט לא ייצמד לשפת המסך. */
const VIEWPORT_MARGIN = 8;

export default function Dropdown({
  trigger,
  children,
  isOpen: externalIsOpen,
  setIsOpen: setExternalIsOpen,
  contentAlign = 'end',
  closeOnContentClick = false,
  activeTriggerClassName,
}: DropdownProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(!!externalIsOpen);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const isOpen = externalIsOpen || internalIsOpen;

  useEffect(() => {
    if (externalIsOpen !== undefined) {
      setInternalIsOpen(externalIsOpen);
    }
  }, [externalIsOpen]);

  const setIsOpen = (value: boolean) => {
    setExternalIsOpen?.(value);
    setInternalIsOpen(value);
  };

  /*
   * המיקום נמדד מהטריגר בכל פתיחה, ומתעדכן בגלילה ובשינוי גודל.
   * ⚠️ contentAlign נשמר כמשמעותו הקודמת: 'start' יישר את התוכן
   * לתחילת הטריגר ו-'end' לסופו — ובעמוד RTL "תחילה" היא ימין.
   * המדידה נעשית בפיקסלים של החלון, ולכן היא מתורגמת כאן במפורש
   * ולא נשענת על כיוון הכתיבה.
   */
  const updatePosition = useCallback(() => {
    const triggerEl = containerRef.current;
    const contentEl = contentRef.current;
    if (!triggerEl) return;

    const rect = triggerEl.getBoundingClientRect();
    const width = contentEl?.offsetWidth ?? 0;
    const height = contentEl?.offsetHeight ?? 0;
    const isRtl = getComputedStyle(triggerEl).direction === 'rtl';

    // 'start' בעמוד RTL הוא הקצה הימני של הטריגר.
    const alignToRightEdge = isRtl ? contentAlign === 'start' : contentAlign === 'end';
    let left = alignToRightEdge ? rect.right - width : rect.left;

    // לא לגלוש מהחלון בשני הכיוונים.
    left = Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN);
    left = Math.max(VIEWPORT_MARGIN, left);

    // נפתח כלפי מעלה כשאין מקום מתחת — אבל רק אם למעלה יש יותר.
    let top = rect.bottom;
    const spaceBelow = window.innerHeight - rect.bottom;
    if (height && spaceBelow < height + VIEWPORT_MARGIN && rect.top > spaceBelow) {
      top = Math.max(VIEWPORT_MARGIN, rect.top - height);
    }

    setPosition({ top, left });
  }, [contentAlign]);

  // מדידה לפני הציור, אחרת התפריט מהבהב בפינה השמאלית העליונה בפריים הראשון.
  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const onChange = () => updatePosition();
    // capture: גם גלילה של מיכל פנימי צריכה להזיז את התפריט, לא רק של החלון.
    window.addEventListener('scroll', onChange, true);
    window.addEventListener('resize', onChange);
    return () => {
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
    };
  }, [isOpen, updatePosition]);

  /*
   * ⚠️ הלחיצה-בחוץ בודקת את **שני** הצמתים. מרגע שהתוכן יושב על ה-body
   * הוא כבר לא בתוך containerRef, ובדיקה שלו בלבד הייתה סוגרת את
   * התפריט בכל לחיצה בתוך התפריט עצמו — כלומר אי אפשר היה לסמן אף
   * אפשרות.
   */
  useOnClickOutside(containerRef as RefObject<HTMLDivElement>, (event) => {
    const target = event.target as Node | null;
    if (target && contentRef.current?.contains(target)) return;
    setIsOpen(false);
  });

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (closeOnContentClick) {
      setIsOpen(false);
    }
  };

  const modifiedTrigger = isValidElement(trigger) ? (
    cloneElement(trigger as React.ReactElement<React.HTMLAttributes<HTMLElement>>, {
      onClick: handleTriggerClick,
      className: `${(trigger as React.ReactElement<React.HTMLAttributes<HTMLElement>>).props.className ?? ''} ${isOpen ? (activeTriggerClassName ?? '') : ''}`,
    })
  ) : (
    <div onClick={handleTriggerClick}>{trigger}</div>
  );

  return (
    <div ref={containerRef} className="relative">
      {modifiedTrigger}
      {isOpen &&
        createPortal(
          <div
            ref={contentRef}
            className="fixed z-50"
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              // עד שהמיקום נמדד התפריט קיים אך שקוף, אחרת הוא נראה
              // לרגע בפינה לפני שהוא קופץ למקומו.
              visibility: position ? 'visible' : 'hidden',
            }}
            onClick={handleContentClick}
          >
            {children}
          </div>,
          document.body
        )}
    </div>
  );
}
